import { Alert, Drawer, Modal, Skeleton, Typography } from 'antd'
import { useState } from 'react'
import { useCuratedTemplateQuery } from '../../../domain/curatedTemplates/curatedTemplatesQueries'
import CuratedTemplateEditor from './CuratedTemplateEditor'
import { formatLastUpdatedLabel } from './curatedTemplatesDisplayModel'
import IntercomSpacer from '../../IntercomSpacer'

type Props = {
  open: boolean
  curatedTemplateId: string | null
  onClose: () => void
  onClosed: () => void
}

const CuratedTemplateDrawer = ({ open, curatedTemplateId, onClose, onClosed }: Props) => {
  const [modal, modalContextHolder] = Modal.useModal()
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const templateQuery = useCuratedTemplateQuery(curatedTemplateId ?? undefined)
  const isCreating = curatedTemplateId === null
  const isLoadingTemplate = !isCreating && templateQuery.isLoading
  const lastUpdatedAt = isCreating ? '' : formatLastUpdatedLabel(templateQuery.data?.lastUpdatedAt)

  const close = () => {
    setHasUnsavedChanges(false)
    onClose()
  }

  const requestClose = () => {
    if (!hasUnsavedChanges) {
      close()
      return
    }
    modal.confirm({
      title: 'Discard your unsaved changes to this template?',
      okText: 'Discard',
      cancelText: 'Keep Editing',
      okButtonProps: { danger: true },
      onOk: close
    })
  }

  const renderBody = () => {
    if (isLoadingTemplate) {
      return <Skeleton active />
    }
    if (!isCreating && !templateQuery.data) {
      return <Alert type="error" showIcon message="This curated template could not be loaded." />
    }
    return <CuratedTemplateEditor
      template={isCreating ? undefined : templateQuery.data}
      onUnsavedChangesChange={setHasUnsavedChanges}
      onSaved={close}
      onCancel={requestClose}
    />
  }

  return <Drawer
    title={isCreating ? 'New Curated Template' : 'Edit Curated Template'}
    placement="right"
    width={1100}
    open={open}
    onClose={requestClose}
    afterOpenChange={isOpen => {
      if (!isOpen) {
        onClosed()
      }
    }}
    destroyOnClose
  >
    {modalContextHolder}
    {renderBody()}
    {lastUpdatedAt && <Typography.Paragraph type="secondary" style={{ marginTop: 24, marginBottom: 0, textAlign: 'right' }}>
      {lastUpdatedAt}
    </Typography.Paragraph>}
    <IntercomSpacer />
  </Drawer>
}

export default CuratedTemplateDrawer
